import { ButtonGroup, Flex, useDisclosure } from '@chakra-ui/react';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import Card from 'components/card/Card';
import CheckTable from 'components/dataTables/CheckTable';
import { FileUploadModal } from 'components/modal/fileUpload/FileUploadModal';
import { useGlobalContext } from 'contexts/GlobalContext';
import { SidebarHelpContext } from 'contexts/SidebarHelpContext';
import { ISidebarHelp } from 'models/sidebarHelp/sidebarHelp.model';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { URL_CT360, URL_FALE_CONOSCO } from 'utils/consts/global';
import {
	documentFormatters,
	DocumentType,
} from 'utils/functions/formatDocuments';

import { useDownloadTemplateOpportunity } from './hooks/useDownloadTemplateOppotunity';
import { useUploadOpportunity } from './hooks/useUploadOpportunity';
import { columnsDataOportunidades } from './variables/columnsDataOportunidades';

interface IOpportunity {
	id: number;
	codigo: string;
	sequencia_codigo: number;
	data: string;
	mes_ano_ciclo: string;
	nome_cliente: string;
	cpf: string;
	cnpj: string;
	nome_familia: string;
	nome_sub_familia: string;
	nome_modelo: string;
	nome_produto: string;
	codigo_produto: string;
	nome_mercado: string;
	nome_canal_venda: string;
	nome_forca_venda: string;
	nome_zona_venda: string;
	quantidade: number;
	nome_membro: string;
	receita_bruta: number;
	data_registro: string;
	tipo_documento: DocumentType;
	documento: string;
	code_hierarquia_comercial: string;
}

export default function CadastroOportunidades() {
	const { isOpen, onOpen, onClose } = useDisclosure();

	const history = useHistory();

	const { pathCompanyName } = useGlobalContext();

	const { setters } = useContext(SidebarHelpContext);

	const { setDataSidebar } = setters;

	const tableColumns = useMemo(() => columnsDataOportunidades, []);

	const [hasBaseData, setHasBaseData] = useState<boolean>(false);

	const helpOportunidades: ISidebarHelp = useMemo(
		() => ({
			title: 'Oportunidades',
			firstParagraph:
				'Nesta tela é possível visualizar todas as oportunidades de negócios no tempo da sua indústria. Estas oportunidades, geralmente, vêm do seu CRM em estágio mais avançado de negociação.',
			secondParagraph:
				'Acesse o botão “Cadastrar Oportunidades” para adicionar uma nova oportunidade ou “Importar Oportunidades” para subir grande quantidade de dados.',
			firstLink: URL_CT360,
			secondLink: URL_FALE_CONOSCO,
		}),
		[]
	);

	const dataDelete = useMemo(
		() => ({
			provider: 'opportunity',
			toastSuccessTitle: 'Excluida com sucesso!',
			toastSuccessDescription: 'A oportunidade foi excluida com sucesso.',
			toastErrorTitle: 'Erro ao excluir oportunidade!',
			title: 'Deseja realmente excluir esta oportunidade?',
			text: [
				'Você está prestes a excluir esta oportunidade da sua base de cadastros. Excluir esta oportunidade resultará na remoção dos seguintes itens vinculados a ela:',
				'REPLACE_ME',
				'Você tem certeza que deseja excluir?',
			],
			invalidate: 'oportunidade-list',
		}),
		[]
	);

	const deleteMessage = useMemo(
		() => [
			'Você está prestes a excluir estas oportunidades da sua base de cadastros. Excluir estas oportunidades resultará na remoção dos seguintes itens vinculados a ela:',
			'REPLACE_ME',
		],
		[]
	);

	const dataDeleteAll = useMemo(
		() => ({
			provider: 'opportunity',
			toastSuccessTitle: 'Registros excluídos com sucesso!',
			toastSuccessDescription:
				'As oportunidades foram excluídas com sucesso',
			toastErrorTitle: 'Erro ao excluir oportunidades!',
			invalidate: 'oportunidade-list',
		}),
		[]
	);

	const goToCadastroOportunidades = useCallback(() => {
		history.push(`/${pathCompanyName}/rotina/oportunidades/criar`);
	}, [history, pathCompanyName]);

	const renderRow = useCallback((opportunity: IOpportunity) => {
		return {
			id: opportunity?.id,
			codigoOportunidade: opportunity?.codigo,
			sequenciaCodigoOportunidade: opportunity?.sequencia_codigo,
			competencia: moment(opportunity?.data, 'YYYY/MM/DD')?.format(
				'DD/MM/YYYY'
			),
			cicloAno: opportunity?.mes_ano_ciclo,
			cliente: opportunity?.nome_cliente,
			cnpjCpfId: documentFormatters(
				opportunity?.tipo_documento,
				opportunity?.documento
			),
			familia: opportunity?.nome_familia,
			subFamilia: opportunity?.nome_sub_familia,
			modelo: opportunity?.nome_modelo,
			produto: opportunity?.nome_produto,
			codigoProduto: opportunity?.codigo_produto,
			mercado: opportunity?.nome_mercado,
			canalVenda: opportunity?.nome_canal_venda,
			forcaVenda: opportunity?.nome_forca_venda,
			zonaVenda: opportunity?.nome_zona_venda,
			quantidade: opportunity?.quantidade?.toLocaleString('pt-BR'),
			membro: opportunity?.nome_membro,
			receitaBruta: opportunity?.receita_bruta
				? opportunity?.receita_bruta?.toLocaleString?.('pt-BR', {
						style: 'currency',
						currency: 'BRL',
				  })
				: 'R$ 0,00',
			dataRegistro: opportunity?.data_registro
				? moment
						.utc(opportunity.data_registro)
						.local()
						.format('DD/MM/YYYY HH:mm:ss')
				: '',
			code: opportunity?.code_hierarquia_comercial || '',
		};
	}, []);

	const renderButtons = useCallback(() => {
		return (
			<Flex
				flexDirection={'row'}
				justifyContent={'flex-end'}
				mt={'20px'}
				me={'20px'}
				mb={'40px'}
			>
				<ButtonGroup spacing={'20px'}>
					<ButtonComponent
						data-test={'button-importar-oportunidades'}
						type={'ghost'}
						title={'Importar Oportunidades'}
						onClick={onOpen}
					/>
					<FileUploadModal
						isOpen={isOpen}
						onClose={onClose}
						headerText={'Importar Oportunidades'}
						module={'oportunidades'}
						useDownloadTemplate={useDownloadTemplateOpportunity}
						stringInvalidQuery='oportunidade-list'
						useUpload={useUploadOpportunity}
						showLineRestricts={true}
						columnsData={tableColumns}
						hasBaseData={hasBaseData}
					/>
					<ButtonComponent
						data-test={'button-cadastrar-oportunidades'}
						type={'primary'}
						title={'Cadastrar Oportunidades'}
						onClick={goToCadastroOportunidades}
					/>
				</ButtonGroup>
			</Flex>
		);
	}, [
		goToCadastroOportunidades,
		isOpen,
		onClose,
		onOpen,
		tableColumns,
		hasBaseData,
	]);

	function renderTable() {
		return (
			<CheckTable
				dataTest='button-filtrar-oportunidades'
				module={'oportunidades'}
				linkEditForm={`/${pathCompanyName}/rotina/oportunidades/editar`}
				columnsData={tableColumns}
				dataDelete={dataDelete}
				deleteMessage={deleteMessage}
				showGenericSearchInput={false}
				filterModule='oportunidade'
				bulkDeleteProps={dataDeleteAll}
				renderRow={renderRow}
				setHasBaseData={setHasBaseData}
			/>
		);
	}

	useEffect(() => {
		setDataSidebar(helpOportunidades);
	}, [helpOportunidades, setDataSidebar]);

	return (
		<Flex
			direction='column'
			pt={{ sm: '125px', lg: '75px' }}
		>
			<Card px='0px'>
				{renderButtons()}
				{renderTable()}
			</Card>
		</Flex>
	);
}
